import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

// ── HLS / stream helpers ──────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const toProxyUrl = (url) =>
  `${API_URL}/anime/proxy-stream?url=${encodeURIComponent(url)}`;

const DIRECT_EXTS = ['.m3u8', '.mp4', '.webm', '.ogg', '.ts'];
// `kind` lo pone el backend cuando resolvió el embed al video directo (sin anuncios)
const isDirectStream = (src) =>
  Boolean(src?.kind) || Boolean(src?.url && DIRECT_EXTS.some((ext) => src.url.includes(ext)));
const isHlsStream = (src) => src.kind ? src.kind === 'hls' : src.url.includes('.m3u8');

// Si el video directo falla se vuelve al iframe original del host
const toEmbedFallback = (src) => ({
  server: src.server, quality: src.quality, url: src.embedUrl, fallbackOf: src.url,
});

// Servidores que generan popups — se ocultan de la UI (datos intactos)
const POPUP_SERVERS = ['streamwish', 'sw', 'netu', 'fembed', 'stape', 'okru'];
const isPopupServer = (src) => {
  const name = (src.server || src.quality || '').toLowerCase();
  return POPUP_SERVERS.some((blocked) => name === blocked || name.includes(blocked));
};
const filterSources = (srcs) => (srcs || []).filter((s) => !isPopupServer(s));

function useHls(videoRef, src, onFatal) {
  const onFatalRef = useRef(onFatal);
  onFatalRef.current = onFatal;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src || !isDirectStream(src)) return;
    const fail = () => onFatalRef.current?.();

    if (isHlsStream(src)) {
      const streamUrl = toProxyUrl(src.url);
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.on(Hls.Events.ERROR, (_e, data) => { if (data.fatal) fail(); });
        hls.loadSource(streamUrl);
        hls.attachMedia(video);
        return () => hls.destroy();
      }
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = streamUrl;
      }
    } else {
      // Resueltos por el backend van por el proxy (Referer + Range para adelantar)
      video.src = src.kind ? toProxyUrl(src.url) : src.url;
    }
    video.addEventListener('error', fail);
    return () => video.removeEventListener('error', fail);
  }, [videoRef, src]);
}

// ── localStorage fallback helpers (unauthenticated users) ────────────────────
const WATCHED_KEY = 'ashveil_watched';
const HISTORY_KEY = 'ashveil_history';

const getWatchedLocal = () => {
  try { return new Set(JSON.parse(localStorage.getItem(WATCHED_KEY) || '[]')); }
  catch { return new Set(); }
};

const markWatchedLocal = (id) => {
  const s = getWatchedLocal();
  s.add(id);
  localStorage.setItem(WATCHED_KEY, JSON.stringify([...s]));
};

const saveToHistoryLocal = ({ animeId, animeTitle, episodeId, epNum, title }) => {
  if (!animeId) return;
  try {
    const prev = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    const entry = { animeId, animeTitle, episodeId, epNum, title, ts: Date.now() };
    localStorage.setItem(
      HISTORY_KEY,
      JSON.stringify([entry, ...prev.filter((h) => h.animeId !== animeId)].slice(0, 20))
    );
  } catch {}
};

// ── API helpers (authenticated users) ────────────────────────────────────────
const userFetch = (path, opts = {}) => {
  const token = localStorage.getItem('token');
  return fetch(`${API_URL}/user${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};

// ── Timing constants ──────────────────────────────────
const TOAST_AFTER_MS   = 20 * 60 * 1000; // 20 min inactivity → show "¿Terminaste?" toast

// ── Component ─────────────────────────────────────────
export default function Watch() {
  const { episodeId } = useParams();
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const { user }        = useAuth();

  const title   = searchParams.get('title')  || 'Reproduciendo';
  const animeId = searchParams.get('animeId');
  const epNum   = parseInt(searchParams.get('ep') || '0', 10);

  // ── Refs ─────────────────────────────────────────────
  const videoRef           = useRef(null);
  const iframeRef          = useRef(null);
  const nextEpRef          = useRef(null);
  const currentEpRowRef    = useRef(null);
  const countdownTimerRef  = useRef(null);
  const inactivityIntRef   = useRef(null);
  const lastActivityRef    = useRef(Date.now());
  const episodeStartRef    = useRef(Date.now());
  const toastShownAtRef    = useRef(null);
  const inactivityToastRef = useRef(false);
  const hasMarkedWatchedRef = useRef(false); // prevents duplicate markWatched calls from timeupdate

  // ── State ─────────────────────────────────────────────
  const [mundos,            setMundos]            = useState({});
  const [activeMundo,       setActiveMundo]       = useState('mundo1');
  const [sources,           setSources]           = useState([]);
  const [selected,          setSelected]          = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState(null);
  const [episodes,          setEpisodes]          = useState([]);
  const [watched,           setWatched]           = useState(() => user ? new Set() : getWatchedLocal());
  const [showEpList,        setShowEpList]        = useState(false);
  const [autoplayCountdown, setAutoplayCountdown] = useState(null);
  const [inactivityToast,   setInactivityToast]   = useState(false);
  // ── Derived from URL params — used by effects below ──
  const animeTitle = title.split(' — ')[0];
  const epNavLink  = (ep) =>
    `/watch/${encodeURIComponent(ep.id)}` +
    `?title=${encodeURIComponent(`${animeTitle} — Ep ${ep.number}`)}` +
    `&animeId=${animeId}&ep=${ep.number}`;

  // ── Load watched set from API (authenticated) ─────────
  useEffect(() => {
    if (!user) return;
    userFetch('/watched')
      .then((r) => r.json())
      .then((ids) => setWatched(new Set(ids)))
      .catch(() => {});
  }, [user]);

  // ── HLS ──────────────────────────────────────────────
  useHls(videoRef, selected, () => {
    if (selected?.embedUrl) setSelected(toEmbedFallback(selected));
  });

  // ── Cleanup all timers on unmount ─────────────────────
  useEffect(() => () => {
    clearInterval(countdownTimerRef.current);
    clearInterval(inactivityIntRef.current);
  }, []);

  // ── Reset per-episode state ───────────────────────────
  useEffect(() => {
    lastActivityRef.current     = Date.now();
    episodeStartRef.current     = Date.now();
    toastShownAtRef.current     = null;
    inactivityToastRef.current  = false;
    hasMarkedWatchedRef.current = false;
    setInactivityToast(false);
    setAutoplayCountdown(null);
    clearInterval(countdownTimerRef.current);
    if (!user) setWatched(getWatchedLocal());
  }, [episodeId, user]);

  // ── Load stream sources ───────────────────────────────
  useEffect(() => {
    setLoading(true);
    setMundos({});
    setActiveMundo('mundo1');

    const fetchSources = animeId && epNum
      ? fetch(`${API_URL}/anime/${animeId}/episode/${epNum}`).then((r) => r.json())
      : api.anime.watch(decodeURIComponent(episodeId))
          .then((data) => ({ mundos: { mundo1: data.sources || [] } }));

    fetchSources
      .then((data) => {
        const newMundos = data.mundos || {};
        setMundos(newMundos);
        // Prefer the first mundo that has at least one non-popup server
        const firstKey =
          Object.keys(newMundos).find((k) => filterSources(newMundos[k]).length > 0) ||
          Object.keys(newMundos).find((k) => newMundos[k]?.length > 0) ||
          'mundo1';
        setActiveMundo(firstKey);
        const srcs = filterSources(newMundos[firstKey] || []);
        setSources(srcs);
        const def = srcs.find((s) => s.quality === 'default') || srcs[0];
        setSelected(def || null);

        const curAnimeTitle = title.split(' — ')[0];
        if (user) {
          userFetch('/history', {
            method: 'POST',
            body: JSON.stringify({
              animeId, episodeId, animeTitle: curAnimeTitle, epNum,
              episodeTitle: `Episodio ${epNum}`,
            }),
          }).catch(() => {});
          if (animeId) {
            userFetch('/now-watching', {
              method: 'PUT',
              body: JSON.stringify({ animeId, animeTitle: curAnimeTitle }),
            }).catch(() => {});
          }
        } else {
          saveToHistoryLocal({ animeId, animeTitle: curAnimeTitle, episodeId, epNum, title });
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [episodeId]);

  // ── Load episode list for navigation ─────────────────
  useEffect(() => {
    if (!animeId) return;
    api.anime.episodes(animeId)
      .then((eps) => setEpisodes(eps || []))
      .catch(() => {});
  }, [animeId]);

  // ── Global activity tracking ──────────────────────────
  // Mounted once — refs are always current so no deps needed.
  // Scroll/click resets the inactivity timer and dismisses the toast if showing.
  useEffect(() => {
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      if (inactivityToastRef.current) {
        inactivityToastRef.current = false;
        toastShownAtRef.current    = null;
        setInactivityToast(false);
      }
    };
    document.addEventListener('click',  onActivity, { capture: true });
    document.addEventListener('scroll', onActivity, { capture: true, passive: true });
    return () => {
      document.removeEventListener('click',  onActivity, { capture: true });
      document.removeEventListener('scroll', onActivity, { capture: true });
    };
  }, []);

  // ── Inactivity checker ────────────────────────────────
  // Runs every 15s. Shows toast after 20 min of no scroll/click.
  useEffect(() => {
    clearInterval(inactivityIntRef.current);

    inactivityIntRef.current = setInterval(() => {
      const now      = Date.now();
      const inactive = now - lastActivityRef.current;

      if (!nextEpRef.current) return;

      if (!inactivityToastRef.current) {
        if (inactive >= TOAST_AFTER_MS) {
          inactivityToastRef.current = true;
          toastShownAtRef.current    = now;
          setInactivityToast(true);
        }
      } else {
        // toast visible — el usuario decide si continúa
      }
    }, 15_000);

    return () => clearInterval(inactivityIntRef.current);
  }, [episodeId, animeTitle, animeId, epNum, navigate, user]);

  // ── Switch mundo (provider) ───────────────────────────
  const switchMundo = (key) => {
    setActiveMundo(key);
    const srcs = filterSources(mundos[key] || []);
    setSources(srcs);
    const def = srcs.find((s) => s.quality === 'default') || srcs[0] || null;
    setSelected(def);
  };

  // ── Switch server ─────────────────────────────────────
  const switchSource = (src) => {
    const time = videoRef.current?.currentTime || 0;
    setSelected(src);
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = time;
        videoRef.current.play().catch(() => {});
      }
    }, 200);
  };

  // ── Derived episode data ──────────────────────────────
  const sortedEps  = [...episodes].sort((a, b) => (a.number || 0) - (b.number || 0));
  const currentIdx = sortedEps.findIndex((e) => e.number === epNum);
  const prevEp     = currentIdx > 0 ? sortedEps[currentIdx - 1] : null;
  const nextEp     = currentIdx !== -1 && currentIdx < sortedEps.length - 1
    ? sortedEps[currentIdx + 1] : null;

  nextEpRef.current = nextEp;

  // ── Autoplay: 'ended' event on native <video> only ───
  // Cross-origin iframes can't fire ended events — those use the inactivity toast instead.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selected || !isDirectStream(selected)) return;

    const handleEnded = () => {
      if (user) {
        userFetch('/history', {
          method: 'POST',
          body: JSON.stringify({ animeId, episodeId, epNum }),
        }).catch(() => {});
      } else {
        markWatchedLocal(episodeId);
      }
      setWatched((prev) => new Set([...prev, episodeId]));

      if (!nextEpRef.current) return;

      let count = 5;
      setAutoplayCountdown(count);
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = setInterval(() => {
        count -= 1;
        if (count <= 0) {
          clearInterval(countdownTimerRef.current);
          setAutoplayCountdown(null);
          const next = nextEpRef.current;
          if (next) {
            navigate(
              `/watch/${encodeURIComponent(next.id)}` +
              `?title=${encodeURIComponent(`${animeTitle} — Ep ${next.number}`)}` +
              `&animeId=${animeId}&ep=${next.number}`
            );
          }
        } else {
          setAutoplayCountdown(count);
        }
      }, 1000);
    };

    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [selected, episodeId, animeTitle, animeId, epNum, navigate, user]);

  // ── timeupdate: mark watched when ≥85% or ≤2 min remaining ─────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selected || !isDirectStream(selected)) return;

    const handleTimeUpdate = () => {
      if (!video.duration || hasMarkedWatchedRef.current) return;
      const percent   = video.currentTime / video.duration;
      const remaining = video.duration - video.currentTime;
      if (percent >= 0.85 || remaining <= 120) {
        hasMarkedWatchedRef.current = true;
        setWatched((prev) => {
          if (prev.has(episodeId)) return prev;
          if (user) {
            userFetch('/history', {
              method: 'POST',
              body: JSON.stringify({ animeId, episodeId, epNum }),
            }).catch(() => {});
          } else {
            markWatchedLocal(episodeId);
          }
          return new Set([...prev, episodeId]);
        });
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [selected, episodeId, animeId, epNum, user]);

  const cancelAutoplay = () => {
    clearInterval(countdownTimerRef.current);
    setAutoplayCountdown(null);
  };

  const cancelInactivityToast = () => {
    inactivityToastRef.current = false;
    toastShownAtRef.current    = null;
    lastActivityRef.current    = Date.now(); // reset so toast doesn't immediately reappear
    setInactivityToast(false);
  };

  // ── Close episode panel on Escape ─────────────────────
  useEffect(() => {
    if (!showEpList) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowEpList(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showEpList]);

  // ── Scroll current episode into view when panel opens ─
  useEffect(() => {
    if (!showEpList) return;
    const t = setTimeout(() => {
      currentEpRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(t);
  }, [showEpList]);

  // ── Early returns ─────────────────────────────────────
  if (loading) return <div className="watch-page"><div className="state-center">Cargando stream...</div></div>;
  if (error)   return <div className="watch-page"><div className="state-center state-error">Error: {error}</div></div>;
  if (!selected) return <div className="watch-page"><div className="state-center">No hay fuentes disponibles.</div></div>;

  return (
    <div className="watch-page">
      {/* ── Player ───────────────────────────────────── */}
      <div className="watch-player">
        {isDirectStream(selected) ? (
          <video ref={videoRef} controls autoPlay>
            Tu navegador no soporta la reproducción.
          </video>
        ) : (
          <iframe
            ref={iframeRef}
            key={selected.url}
            src={selected.url}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            title={title}
            sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
          />
        )}
      </div>

      {/* ── Manual mark-watched button ───────────────── */}
      <div className="watch-manual-mark">
        <button
          className={`manual-mark-btn${watched.has(episodeId) ? ' manual-mark-btn--done' : ''}`}
          onClick={() => {
            if (watched.has(episodeId)) return;
            if (user) {
              userFetch('/history', {
                method: 'POST',
                body: JSON.stringify({ animeId, episodeId, epNum }),
              }).catch(() => {});
            } else {
              markWatchedLocal(episodeId);
            }
            hasMarkedWatchedRef.current = true;
            setWatched((prev) => new Set([...prev, episodeId]));
          }}
        >
          {watched.has(episodeId) ? '✓ Marcado como visto' : '✓ Marcar este episodio como visto'}
        </button>
      </div>

      {/* ── Native video autoplay countdown ──────────── */}
      {autoplayCountdown !== null && nextEp && (
        <div className="autoplay-toast">
          <span>Siguiente episodio en <strong>{autoplayCountdown}s</strong></span>
          <button className="autoplay-toast__cancel" onClick={cancelAutoplay}>
            Cancelar
          </button>
        </div>
      )}

      {/* ── Inactivity toast (iframe fallback) ───────── */}
      {inactivityToast && nextEp && (
        <div className="inactivity-toast">
          <span>¿Terminaste? → <strong>Ep {nextEp.number}</strong></span>
          <Link to={epNavLink(nextEp)} className="inactivity-toast__continue">
            Continuar
          </Link>
          <button className="inactivity-toast__cancel" onClick={cancelInactivityToast}>
            Cancelar
          </button>
        </div>
      )}

      {/* ── Info + Sidebar ───────────────────────────── */}
      <div className="watch-layout">
        <div>
          <p className="watch-info__label">Reproduciendo</p>
          <div className="watch-info__title-row">
            {animeId && (
              <Link to={`/anime/${animeId}`} className="watch-back-btn">← Volver</Link>
            )}
            <h1 className="watch-info__title">{title}</h1>
          </div>

          {Object.keys(mundos).length > 0 && (
            <div className="watch-servers">
              {Object.keys(mundos).filter((k) => filterSources(mundos[k]).length > 0).length > 1 && (
                <div className="mundo-selector">
                  <span className="watch-servers__label">Mundo:</span>
                  {Object.keys(mundos)
                    .filter((key) => filterSources(mundos[key]).length > 0)
                    .map((key) => (
                      <button
                        key={key}
                        className={`mundo-btn${activeMundo === key ? ' mundo-btn--active' : ''}`}
                        onClick={() => switchMundo(key)}
                      >
                        {key === 'mundo1' ? 'Mundo 1' : key === 'mundo2' ? 'Mundo 2' : 'Mundo 3'}
                        <span className="mundo-count">{filterSources(mundos[key]).length}</span>
                      </button>
                    ))}
                </div>
              )}
              {sources.length > 0 && (
                <div className="server-selector">
                  <span className="watch-servers__label">Servidor:</span>
                  {sources.map((src) => (
                    <button
                      key={src.url}
                      onClick={() => switchSource(src)}
                      className={`server-btn${src.url === selected?.url || src.url === selected?.fallbackOf ? ' server-btn--active' : ''}`}
                    >
                      {src.server || src.quality || '?'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="watch-sidebar">
          <p className="sidebar-label">Episodios</p>

          {/* Primary CTA ── next episode */}
          {nextEp && (
            <Link to={epNavLink(nextEp)} className="next-ep-cta">
              <div className="next-ep-cta__icon">▶</div>
              <div className="next-ep-cta__body">
                <span className="next-ep-cta__label">Siguiente episodio</span>
                <span className="next-ep-cta__ep">Ep {nextEp.number}</span>
              </div>
              {watched.has(nextEp.id) && (
                <span className="next-ep-cta__badge">✓ Visto</span>
              )}
            </Link>
          )}

          {prevEp && (
            <Link to={epNavLink(prevEp)} className="ep-nav">
              <div className="ep-nav__thumb">◀</div>
              <div className="ep-nav__body">
                <p className="ep-nav__num">Episodio anterior</p>
                <p className="ep-nav__title">Ep {prevEp.number}</p>
              </div>
              {watched.has(prevEp.id) && <span className="ep-nav__watched">✓ Visto</span>}
            </Link>
          )}

          {animeId && (
            <button className="watch-more" onClick={() => setShowEpList(true)}>
              Ver todos los episodios
            </button>
          )}
        </div>
      </div>

      {/* ── Episode list panel ───────────────────────── */}
      {showEpList && (
        <div
          className="ep-panel-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowEpList(false)}
        >
          <div className="ep-panel">
            <div className="ep-panel__header">
              <h2 className="ep-panel__title">{animeTitle} — Episodios</h2>
              <button className="ep-panel__close" onClick={() => setShowEpList(false)}>✕</button>
            </div>

            <div className="ep-panel__body">
              {sortedEps.length === 0 && (
                <p style={{ padding: '2rem', color: 'var(--text-sec)', textAlign: 'center' }}>
                  Cargando episodios...
                </p>
              )}

              {sortedEps.map((ep) => {
                const isCurrent = ep.number === epNum;
                const isWatched = watched.has(ep.id);
                return (
                  <Link
                    key={ep.id}
                    to={epNavLink(ep)}
                    ref={isCurrent ? currentEpRowRef : null}
                    className={`ep-row${isCurrent ? ' ep-row--current' : ''}`}
                    onClick={() => setShowEpList(false)}
                  >
                    <div className="ep-row__thumb">
                      {ep.thumbnail
                        ? <img src={ep.thumbnail} alt="" loading="lazy" />
                        : <span className="ep-row__thumb-icon">▶</span>
                      }
                      {isCurrent && <div className="ep-row__now-playing">▶</div>}
                    </div>

                    <div className="ep-row__info">
                      <p className="ep-row__num">Episodio {ep.number}</p>
                      <p className="ep-row__title">{ep.title}</p>
                    </div>

                    {isWatched && <span className="ep-row__badge">✓ Visto</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
